import React, { useEffect, useState, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { format } from "date-fns";
import {
  Check,
  FileText,
  Target,
  Calendar,
  Pill,
  CreditCard,
  Plus,
  Sparkles,
  AlertTriangle,
  Save,
  Loader,
  CloudCog,
} from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  TreatmentPlan,
  useTreatmentPlanFormState,
} from "@/lib/treatmentPlan/treatmentPlanData";
import { PlanOverviewSection } from "./PlanOverviewSection";
import { GoalsSection } from "./GoalsSection";
import { FollowUpSection } from "./FollowUpSection";
import { cn } from "@/lib/utils";
import {
  updateConsultationActions,
  SaveAIChatMessage,
  setAddPatientAIChatMessage,
  setTreatmentPlanDetails,
  setIncrementTreatmentPlanCompleted,
  setUpdateTreatmentPlanAIChatMessage,
  getPatientConsultationByConsultationId,
  updateEventStatus,
} from "@/dispatchers/patientDispatchers";
import useTitanApp from "@/hooks/useTitanApp";
import {
  updateTreatmentPlan,
  getPatientActiveTreatmentPlan,
  updateTreatmentPlanFollowUpStatus,
  linkAppointmentConsultation,
  addConsultationLinkedTemplate,
} from "@/dispatchers/treatmentPlanDispatchers";
import {
  deleteScheduleEvents,
  addScheduleEvent,
} from "@/dispatchers/scheduleEventsDispatchers";
import { pharmacies } from "@/lib/prescriptionData";
import {
  createTaskDraftFromSchedule,
  createVaccineDraftFromSchedule,
} from "@/components/Scheduler/scheduleData";

// Helper function to format dates to YYYY-MM-DD format for HTML date inputs
const formatDateForInput = (date: string | Date | undefined): string => {
  if (!date) return "";
  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return "";
    return dateObj.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

const planStatusIntToString: Record<number, string> = {
  1: "active",
  2: "completed",
  3: "paused",
  4: "cancelled",
};

const goalStatusIntToString: Record<number, string> = {
  1: "pending",
  2: "in-progress",
  3: "achieved",
  4: "abandoned",
};

const targetDirectionIntToString: Record<number, string> = {
  1: "AtOrAbove",
  2: "AtOrBelow",
  3: "EqualTo",
};

const targetDirectionToInt: Record<string, number> = {
  AtOrAbove: 1,
  AtOrBelow: 2,
  EqualTo: 3,
};

const vitalTypeIntToString: Record<number, string> = {
  1: "weight",
  2: "blood-pressure",
  3: "heart-rate",
  4: "temperature",
  5: "height",
  6: "bmi",
  7: "oxygen-saturation",
  8: "blood-glucose",
};

const normalizeFollowUpAppointments = (appointments: any[] = []): any[] => {
  return appointments.map((apt) => {
    let date = apt.date || "";
    let time = apt.time || "";

    if (!date && apt.startDate) {
      try {
        date = new Date(apt.startDate).toISOString().split("T")[0];
      } catch {
        date = "";
      }
    }

    if (!time && apt.startDate) {
      try {
        const isoString =
          typeof apt.startDate === "string"
            ? apt.startDate
            : new Date(apt.startDate).toISOString();
        const timePart = isoString.split("T")[1];
        if (timePart) {
          time = timePart.substring(0, 5);
        }
      } catch {
        time = "";
      }
    }
    return {
      ...apt,
      date,
      time,
      serviceId: apt.serviceId || apt.serviceID || "",
      patientId: apt.patientId || apt.patientID || "",
      consultationID: apt.consultationID || apt.consultationId || "",
      // roomId already exists on the object, also bind to room field
      // so the room dropdown can match by ID
      duration: apt.duration != null ? String(apt.duration) : "",
    };
  });
};

const normalizePlanStatus = (status: any): string => {
  if (typeof status === "number") {
    return planStatusIntToString[status] ?? "active";
  }

  const normalized = `${status ?? ""}`.trim().toLowerCase();
  if (["active", "completed", "paused", "cancelled"].includes(normalized)) {
    return normalized;
  }

  return "active";
};

const normalizeGoalStatus = (status: any): string => {
  if (typeof status === "number") {
    return goalStatusIntToString[status] ?? "pending";
  }

  const normalized = `${status ?? ""}`
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/\s+/g, "-");

  if (
    ["pending", "in-progress", "achieved", "abandoned"].includes(normalized)
  ) {
    return normalized;
  }

  return "pending";
};

const normalizeVitalType = (vitalType: any): any => {
  if (typeof vitalType === "number") {
    return vitalTypeIntToString[vitalType];
  }

  const normalized = `${vitalType ?? ""}`
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/\s+/g, "-");

  return normalized || undefined;
};

const normalizeGoals = (goals: any[] = []) => {
  return goals.map((goal) => ({
    ...goal,
    status: normalizeGoalStatus(goal?.status),
    linkedVitalType: normalizeVitalType(goal?.linkedVitalType),
    targetDirection:
      typeof goal?.targetDirection === "number"
        ? targetDirectionIntToString[goal.targetDirection] ?? "AtOrBelow"
        : goal?.targetDirection ?? "AtOrBelow",
  }));
};

interface TreatmentPlanModalProps {
  trigger?: React.ReactNode;
  existingPlan?: TreatmentPlan;
  editMode?: boolean;
  selectedPatient?: any;
  consultationId?: string;
  existingPrescriptions?: Array<{ id: string; data: any }>;
  existingAppointments?: Array<{ id: string; data: any }>;
  schedulePharmacists?: any[];
  scheduleRoomNames?: string[];
  onSave: (data: TreatmentPlan) => void;
  onSaveDraft?: (data: any) => void;
  draftData?: any;
  isDarkMode?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  consultationNotes?: string;
  summaryText?: string;
  //   services?: Service[];
  services?: any[];
  fromConsultationActions?: boolean;
  consultationActionId?: string | null;
}

export const TreatmentPlanModal: React.FC<TreatmentPlanModalProps> = ({
  trigger,
  existingPlan,
  editMode = false,
  selectedPatient,
  consultationId,
  existingPrescriptions = [],
  existingAppointments = [],
  schedulePharmacists = [],
  scheduleRoomNames = [],
  onSave,
  onSaveDraft,
  draftData,
  isDarkMode = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  consultationNotes = "",
  summaryText = "",
  services = [],
  fromConsultationActions = false,
  consultationActionId = null,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [isAddingGoal, setIsAddingGoal] = useState(false);
  const [isAddingAppointment, setIsAddingAppointment] = useState(false);
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);
  const [isGeneratingWithAI, setIsGeneratingWithAI] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const initialFormDataRef = useRef<any>(null);

  const { toast } = useToast();
  const { patientState, appState } = useTitanApp();
  const { currentUser } = appState || {};
  const {
    consultationId: stateConsultationId,
    selectedPatientDetails,
    consultationData,
  } = patientState || {};
  // prefer the prop, fall back to Redux state
  const resolvedConsultationId = consultationId || stateConsultationId;
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  // Determine if we're viewing a draft
  const isViewingDraft = draftData && !editMode && !existingPlan;

  const {
    formData,
    updateFormData,
    addGoal,
    updateGoal,
    removeGoal,
    addFollowUpAppointment,
    updateFollowUpAppointment,
    removeFollowUpAppointment,
    addLinkedPrescription,
    removeLinkedPrescription,
    setSubscriptionPlan,
    reset,
    currentPharmacyId,
  } = useTreatmentPlanFormState(existingPlan);
  // Load draft data IMMEDIATELY when modal opens with draft (before any other effects)
  useEffect(() => {
    if (open && draftData && !editMode && !existingPlan) {
      // Reset first to clear any previous state
      reset();

      // Then load the complete draft data including nested arrays
      const formattedStartDate = formatDateForInput(draftData.startDate);
      const formattedEndDate = formatDateForInput(draftData.endDate);

      updateFormData({
        name: draftData.name || "",
        description: draftData.description || "",
        startDate: formattedStartDate || new Date().toISOString().split("T")[0],
        endDate: formattedEndDate || "",
        status: normalizePlanStatus(draftData.status) as any,
        goals: normalizeGoals(draftData.goals || []),
        followUpAppointments: normalizeFollowUpAppointments(
          // ← changed
          draftData.followUpAppointments || [],
        ),
        linkedPrescriptions: draftData.linkedPrescriptions || [],
        subscriptionPlan: draftData.subscriptionPlan,
      });
      if (draftData.pendingGoals?.length > 0) {
        setIsAddingGoal(true);
        setOpenAccordionItems((prev) =>
          prev.includes("goals") ? prev : [...prev, "goals"],
        );
      }

      // Appointments from AI never have required fields, always show add form
      if (draftData.pendingAppointments?.length > 0) {
        setIsAddingAppointment(true);
        setOpenAccordionItems((prev) =>
          prev.includes("appointments") ? prev : [...prev, "appointments"],
        );
      }
    }
  }, [open, draftData]);

  // Capture initial state when modal opens and handle close/reset
  useEffect(() => {
    if (open) {
      // Delay capturing initial state to allow draft to load first
      const timer = setTimeout(() => {
        initialFormDataRef.current = JSON.stringify(formData);
      }, 150);
      return () => clearTimeout(timer);
    } else {
      // Reset when modal closes
      reset();
      setOpenAccordionItems([]);
      setIsAddingGoal(false);
      setIsAddingAppointment(false);
      initialFormDataRef.current = null;
      setPendingClose(false);
      setShowUnsavedWarning(false); // CRITICAL: Reset warning dialog state
    }
  }, [open]);

  // Check if form has unsaved changes
  useEffect(() => {
    if (open && editMode && existingPlan) {
      const formattedStartDate = formatDateForInput(
        (existingPlan as any).startDate,
      );
      const formattedEndDate = formatDateForInput(
        (existingPlan as any).endDate,
      );

      updateFormData({
        name: (existingPlan as any).name || "",
        description: (existingPlan as any).description || "",
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        status: normalizePlanStatus((existingPlan as any).status) as any,
        goals: normalizeGoals((existingPlan as any).goals || []),
        followUpAppointments: normalizeFollowUpAppointments(
          // ← changed
          (existingPlan as any).followUpAppointments || [],
        ),
        linkedPrescriptions: (existingPlan as any).linkedPrescriptions || [],
        subscriptionPlan: (existingPlan as any).subscriptionPlan,
      });
    }
  }, [open, editMode, existingPlan]);
  const isDirty = () => {
    if (!initialFormDataRef.current) return false;
    const current = JSON.stringify(formData);
    return current !== initialFormDataRef.current;
  };

  // Handle close with dirty state check
  const handleClose = () => {
    if (isDirty() && onSaveDraft) {
      const draftPayload = {
        ...formData,
        isDraft: true,
        savedAt: new Date().toISOString(),
        // Preserve pendingGoals/pendingAppointments from the original AI draft
        // so re-opening the draft card still shows the pre-filled forms
        pendingGoals: draftData?.pendingGoals || [],
        pendingAppointments: draftData?.pendingAppointments || [],
      };
      onSaveDraft(draftPayload);
      toast({
        title: "Draft Auto-Saved",
        description: "Your treatment plan draft has been automatically saved.",
      });
    }
    setOpen(false);
  };

  // Handle discard changes
  const handleDiscardChanges = () => {
    // AlertDialogCancel will close the dialog automatically
    // Just close the main modal
    setOpen(false);
  };

  // Handle save as draft
  const handleSaveDraft = () => {
    if (onSaveDraft) {
      // Enrich appointment IDs with full appointment data for display
      const draftPayload = {
        ...formData,
        isDraft: true,
        savedAt: new Date().toISOString(),
        // Add enriched data for display purposes
        _enrichedAppointments: formData.followUpAppointments?.map(
          (aptId: string) => {
            const appointment = existingAppointments.find(
              (apt: any) => apt.id === aptId,
            );
            return appointment
              ? { id: aptId, data: appointment.data }
              : { id: aptId, data: null };
          },
        ),
      };
      onSaveDraft(draftPayload);
      // AlertDialogAction will close the dialog automatically
      // Just close the main modal and show toast
      setOpen(false);
      toast({
        title: "Draft Saved",
        description:
          "Your treatment plan draft has been saved. You can resume it from the actions panel.",
      });
    }
  };

  // Prepare prescription options
  const statusToInt: Record<string, number> = {
    active: 1,
    completed: 2,
    paused: 3,
    cancelled: 4,
  };

  // Add alongside statusToInt
  const vitalTypeToInt: Record<string, number> = {
    weight: 1,
    "blood-pressure": 2,
    "heart-rate": 3,
    temperature: 4,
    height: 5,
    bmi: 6,
    "oxygen-saturation": 7,
    "blood-glucose": 8,
  };

  const goalStatusToInt: Record<string, number> = {
    pending: 1,
    "in-progress": 2,
    achieved: 3,
    abandoned: 4,
  };

  // Find the actionID by matching treatmentPlanId in consultationData actions
  const existingTreatmentPlanId =
    (existingPlan as any)?.treatmentPlanId || (existingPlan as any)?.id;

  const matchedActionId =
    consultationData?.actions?.find((action: any) => {
      try {
        const parsedPayload = JSON.parse(action.payload || "{}");
        return parsedPayload.treatmentPlanId === existingTreatmentPlanId;
      } catch {
        return false;
      }
    })?.actionID ?? null;

  // ── Edit-mode follow-up appointment delete ──────────────────────────────
  const handleDeleteFollowUpInEditMode = async (apptId: string) => {
    // Check if consultation is completed/locked - cannot delete
    if (
      editMode &&
      stateConsultationId &&
      consultationData?.consultationStatus === 1
    ) {
      toast({
        title: "Cannot Delete",
        description:
          "Cannot modify a completed consultation. If you need to make changes, please contact support.",
        variant: "destructive",
      });
      return;
    }

    // Check if appointment has eventStatus 1 (locked/confirmed - cannot delete)
    const appointmentToDelete = (formData.followUpAppointments || []).find(
      (apt: any) => apt.id === apptId,
    );

    if (appointmentToDelete?.eventStatus === 1) {
      toast({
        title: "Cannot Delete",
        description:
          "This appointment is confirmed and cannot be deleted. Please contact support if you need to cancel it.",
        variant: "destructive",
      });
      return;
    }

    // 1. Cancel the schedule event via API (best-effort)
    try {
      await deleteScheduleEvents(apptId, currentPharmacyId);
    } catch (err) {
      console.warn(
        "deleteScheduleEvents failed, continuing with plan update:",
        err,
      );
    }

    // 2. Remove from local form state
    removeFollowUpAppointment(apptId);

    // 3. Build updated followUp list (exclude deleted appointment)
    const updatedFollowUpAppointments = (
      formData.followUpAppointments || []
    ).filter((a: any) => a.id !== apptId);

    // 4. Update the treatment plan on the backend
    const updatedPlan: any = {
      ...(existingPlan as any),
      treatmentPlanId:
        (existingPlan as any)?.treatmentPlanId || (existingPlan as any)?.id,
      patientId:
        selectedPatientDetails?.patientID ||
        (existingPlan as any)?.patientId ||
        "",
      followUpAppointments: updatedFollowUpAppointments,
      updatedAt: new Date().toISOString(),
    };

    try {
      const requestBody = {
        TreatmentPlan: updatedPlan,
        consultationAction:
          fromConsultationActions && matchedActionId
            ? {
                ActionID: matchedActionId,
                payload: JSON.stringify(updatedPlan),
                actionName: "UpdateTreatmentPlan",
                actionNumber: "15",
                isPerformed: true,
              }
            : null,
      };

      await updateTreatmentPlan(requestBody);
      getPatientActiveTreatmentPlan(selectedPatientDetails?.patientID || "");
      setUpdateTreatmentPlanAIChatMessage({
        sender: "success",
        message: "Treatment Plan Updated Successfully",
        actionId: 15,
        details: updatedPlan,
      });

      toast({
        title: "Appointment Deleted",
        description:
          "The follow-up appointment has been removed from the treatment plan.",
      });
    } catch (err) {
      toast({
        title: "Error",
        description:
          err?.message ||
          "Failed to update treatment plan after deleting appointment.",
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.startDate) {
      toast({
        title: "Validation Error",
        description: "Please enter a plan name and start date",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    const appointmentConsultationIdMap = new Map<string, string>();
    (formData.followUpAppointments || []).forEach((apt: any) => {
      const key = apt.id || "";
      const existing = apt.consultationID || apt.consultationId || "";
      appointmentConsultationIdMap.set(key, existing || uuidv4());
    });

    const treatmentPlan: any = {
      treatmentPlanId:
        (existingPlan as any)?.treatmentPlanId ||
        (existingPlan as any)?.id ||
        uuidv4(),
      name: formData.name!,
      description: formData.description || "",
      consultationId:
        resolvedConsultationId || (existingPlan as any)?.consultationId || "",
      patientId:
        selectedPatientDetails?.patientID ||
        (existingPlan as any)?.patientId ||
        "",
      patientName:
        selectedPatientDetails?.patientName ||
        (existingPlan as any)?.patientName ||
        "",
      pharmacyId: currentPharmacyId,
      createdBy: (existingPlan as any)?.createdBy || "Current User",
      createdAt: (existingPlan as any)?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      startDate: formData.startDate!,
      endDate: formData.endDate || undefined,
      status: statusToInt[normalizePlanStatus(formData.status)] ?? 1,
      goals: (formData.goals || []).map((goal) => ({
        ...goal,
        linkedVitalType: normalizeVitalType(goal.linkedVitalType)
          ? vitalTypeToInt[normalizeVitalType(goal.linkedVitalType)] ?? null
          : null,
        status: goalStatusToInt[normalizeGoalStatus(goal.status)] ?? 1,
        targetDirection:
          targetDirectionToInt[goal.targetDirection ?? "AtOrBelow"] ?? 2,
      })),
      followUpAppointments: (formData.followUpAppointments || []).map(
        (appointment) => {
          const newId = appointment.id || uuidv4();
          const durationMinutes = appointment.duration
            ? parseInt(`${appointment.duration}`)
            : 0;
          const followUpConsultationId =
            appointmentConsultationIdMap.get(appointment.id || "") ||
            appointment.consultationID ||
            appointment.consultationId ||
            uuidv4();

          // Construct startDate from date and time fields
          let startDateIso: string | undefined;
          let endDateIso: string | undefined;

          if (appointment.date) {
            // Form date is YYYY-MM-DD, combine with time if available
            const timeStr = appointment.time ? appointment.time : "00:00";
            const [year, month, day] = appointment.date.split("-").map(Number);
            const [hours, minutes] = timeStr.split(":").map(Number);

            // Create local date from user's selected date/time
            const localDateTime = new Date(
              year,
              (month || 1) - 1,
              day || 1,
              hours || 0,
              minutes || 0,
              0,
            );

            // Send as-is with Z suffix - backend handles timezone conversion
            startDateIso = format(localDateTime, "yyyy-MM-dd'T'HH:mm:ss'Z'");

            // Always provide endDate; for zero-duration appointments it matches startDate
            const endDateTime =
              durationMinutes > 0
                ? new Date(localDateTime.getTime() + durationMinutes * 60000)
                : localDateTime;
            endDateIso = format(endDateTime, "yyyy-MM-dd'T'HH:mm:ss'Z'");
          } else if (appointment.startDate) {
            // Fallback to startDate if available
            const startDateTime = new Date(appointment.startDate);
            if (!isNaN(startDateTime.getTime())) {
              startDateIso = format(startDateTime, "yyyy-MM-dd'T'HH:mm:ss'Z'");
              const endDateTime =
                durationMinutes > 0
                  ? new Date(startDateTime.getTime() + durationMinutes * 60000)
                  : startDateTime;
              endDateIso = format(endDateTime, "yyyy-MM-dd'T'HH:mm:ss'Z'");
            }
          }

          return {
            id: newId,
            patientName:
              selectedPatientDetails?.patientName ||
              appointment.patientName ||
              "",
            patientId:
              selectedPatientDetails?.patientID || appointment.patientId || "",
            patientID:
              selectedPatientDetails?.patientID || appointment.patientID || "",
            pharmacist: appointment.pharmacist || "",
            resourceId: appointment.resourceId || "",
            serviceType: appointment.serviceType || "",
            serviceId: appointment.serviceId || "",
            serviceID: appointment.serviceID || appointment.serviceId || "",
            date: appointment.date || "",
            time: appointment.time || "",
            duration: Number.isFinite(Number(appointment.duration))
              ? Number(appointment.duration)
              : 0,
            durationUnit: "m",
            room: appointment.room || "",
            roomId: appointment.roomId || "",
            notes: appointment.notes || "",
            email: appointment.email || "",
            mobile: appointment.mobile || "",
            confirmationMethod: appointment.confirmationMethod || "",
            consultationID: followUpConsultationId,
            tasksToComplete: appointment.tasksToComplete || [],
            vaccinesAdminister: appointment.vaccinesAdminister || [],
            startDate: startDateIso,
            endDate: endDateIso,
            eventStatus: 2,
            scheduleEventType: 1,
          };
        },
      ),
      linkedPrescriptions: formData.linkedPrescriptions || [],
      ...(editMode && {
        consultationHistory: (formData.followUpAppointments || []).map(
          (apt: any) => {
            // Use the pre-computed ID: existing keeps its own, new gets the UUID assigned above
            const consultationId =
              appointmentConsultationIdMap.get(apt.id || "") ||
              apt.consultationID ||
              apt.consultationId ||
              "";
            const existingHistoryEntry = (existingPlan as any)?.consultationHistory?.find(
              (entry: any) => entry.consultationId === consultationId,
            );
            return {
              consultationId,
              isConsultationCompleted:
                existingHistoryEntry?.isConsultationCompleted ?? false,
            };
          },
        ),
      }),
    };

    try {
      if (editMode) {
        for (const apt of treatmentPlan.followUpAppointments) {
          const scheduleEvent = {
            id: apt.id,
            consultationID: apt.consultationID || "",
            name: apt.notes || "",
            patientID: apt.patientID || apt.patientId || "",
            startDate: apt.startDate || "",
            endDate: apt.endDate || "",
            duration:
              typeof apt.duration === "string"
                ? parseInt(apt.duration, 10) || 0
                : apt.duration || 0,
            durationUnit: "m",
            cls: "",
            recurrenceRule: "",
            exceptionDates: [] as string[],
            resourceId: apt.resourceId || "",
            allDay: false,
            scheduleEventType: 1,
            serviceID: apt.serviceID || apt.serviceId || "",
            email: apt.email || "",
            mobile: apt.mobile || "",
            confirmationMethod: apt.confirmationMethod || "",
            RoomId: apt.roomId || "",
            recordedAt: new Date().toLocaleString(),
            tasksToComplete: apt.tasksToComplete || [],
            vaccinesAdminister: apt.vaccinesAdminister || [],
          };
          try {
            await addScheduleEvent(scheduleEvent, currentPharmacyId);
          } catch (scheduleErr) {
            console.warn(
              "Failed to add schedule event for follow-up appointment:",
              scheduleErr,
            );
          }
        }
        const requestBody = {
          TreatmentPlan: treatmentPlan,
          consultationAction:
            fromConsultationActions && matchedActionId
              ? {
                  ActionID: matchedActionId,
                  payload: JSON.stringify(treatmentPlan),
                  actionName: "UpdateTreatmentPlan",
                  actionNumber: "15",
                  isPerformed: true,
                }
              : null,
        };

        await updateTreatmentPlan(requestBody);
        await getPatientActiveTreatmentPlan(
          selectedPatientDetails?.patientID || "",
        );

        try {
          await addConsultationLinkedTemplate({
            consultationId: resolvedConsultationId,
            templateId: consultationData?.templateId || "",
            templateName: consultationData?.templateName || "",
            patientId:
              selectedPatientDetails?.patientID ||
              (treatmentPlan as any).patientId ||
              "",
            patientName:
              selectedPatientDetails?.patientName ||
              (treatmentPlan as any).patientName ||
              "",
            pharmacyID:
              currentPharmacyId || (treatmentPlan as any).pharmacyId || "",
            createdBy: currentUser?.name || currentUser?.userName || "",
            createdAt:
              (existingPlan as any)?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            treatmentPlan: treatmentPlan,
          });
        } catch (err) {
          console.warn("addConsultationLinkedTemplate (edit) failed:", err);
        }

        setUpdateTreatmentPlanAIChatMessage({
          sender: "success",
          message: "Treatment Plan Updated Successfully",
          actionId: 15,
          details: treatmentPlan, // plain object, matches create pattern
        });
        if (fromConsultationActions) {
          await getPatientConsultationByConsultationId(resolvedConsultationId);
        }
      } else {
        // ── CREATE: existing consultation action flow (unchanged) ──────────────
        if (resolvedConsultationId) {
          const actionPayload = {
            payload: JSON.stringify(treatmentPlan),
            actionName: "CreateTreatmentPlan",
            actionNumber: "15",
            isPerformed: true,
          };
          await updateConsultationActions({
            consultationId: resolvedConsultationId,
            payload: actionPayload,
          });

          try {
            await addConsultationLinkedTemplate({
              consultationId: resolvedConsultationId,
              templateId: consultationData?.templateId || "",
              templateName: consultationData?.templateName || "",
              patientId: selectedPatientDetails?.patientID || "",
              patientName: selectedPatientDetails?.patientName || "",
              pharmacyID: currentPharmacyId || "",
              createdBy: currentUser?.name || currentUser?.userName || "",
              createdAt: new Date().toISOString(),
              treatmentPlan: treatmentPlan,
            });
          } catch (err) {
            console.warn("addConsultationLinkedTemplate (create) failed:", err);
          }

          const chatMessages: any[] = [
            {
              sender: "success",
              message: "Treatment Plan Created Successfully",
              actionId: 15,
              details: {
                payload: JSON.stringify(treatmentPlan),
              },
            },
          ];

          await SaveAIChatMessage({
            consultationChat: JSON.stringify(chatMessages),
            consultationId: resolvedConsultationId,
          });

          setTreatmentPlanDetails(treatmentPlan);
          setIncrementTreatmentPlanCompleted(true);

          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, "0");
          const day = String(now.getDate()).padStart(2, "0");
          const hours = String(now.getHours()).padStart(2, "0");
          const minutes = String(now.getMinutes()).padStart(2, "0");
          const seconds = String(now.getSeconds()).padStart(2, "0");
          const nowFormatted = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;

          const mockScheduleEvent = {
            id: uuidv4(),
            consultationID: resolvedConsultationId || "",
            name: "",
            patientID: selectedPatientDetails?.patientID || "",
            startDate: nowFormatted,
            endDate: nowFormatted,
            duration: 0,
            durationUnit: "m",
            cls: "",
            recurrenceRule: "",
            exceptionDates: [] as string[],
            resourceId: currentUser?.emailAddress,
            allDay: false,
            scheduleEventType: 1,
            serviceID: "6880e77b98b936f8365ab340",
            email: "",
            mobile: "",
            confirmationMethod: "",
            RoomId: "",
            recordedAt: new Date().toLocaleString(),
            tasksToComplete: [],
            vaccinesAdminister: [],
            eventStatus: 3,
          };

          try {
            await addScheduleEvent(mockScheduleEvent, currentPharmacyId);
          } catch (scheduleErr) {
            console.warn(
              "addScheduleEvent (TreatmentPlanModal create) failed:",
              scheduleErr,
            );
          }

          try {
            await updateEventStatus({
              id: mockScheduleEvent.id,
              eventStatus: 3,
            });
          } catch (eventErr) {
            console.warn(
              "updateEventStatus (TreatmentPlanModal create) failed:",
              eventErr,
            );
          }

          try {
            await updateTreatmentPlanFollowUpStatus({
              PatientID: selectedPatientDetails?.patientID || "",
              TreatmentPlanID: treatmentPlan.treatmentPlanId || "",
              FollowUpAppointment: mockScheduleEvent,
            });
          } catch (err) {
            console.warn(
              "updateTreatmentPlanFollowUpStatus (TreatmentPlanModal create) failed:",
              err,
            );
          }

          try {
            await linkAppointmentConsultation({
              PatientID: selectedPatientDetails?.patientID || "",
              TreatmentPlanID: treatmentPlan.treatmentPlanId || "",
              FollowUpAppointment: mockScheduleEvent,
            });
          } catch (err) {
            console.warn(
              "linkAppointmentConsultation (TreatmentPlanModal create) failed:",
              err,
            );
          }
          await getPatientConsultationByConsultationId(resolvedConsultationId);
        }
      }

      onSave(treatmentPlan);
      setOpen(false);

      // Refresh treatment plans list after successful creation in add mode
      if (!editMode && selectedPatientDetails?.patientID) {
        await getPatientActiveTreatmentPlan(selectedPatientDetails.patientID);
      }

      toast({
        title: editMode ? "Treatment Plan Updated" : "Treatment Plan Created",
        description: editMode
          ? "The treatment plan has been updated successfully."
          : "The treatment plan has been created successfully.",
      });
    } catch (err) {
      console.error("Failed to save treatment plan:", err);
      toast({
        title: "Error",
        description:
          err?.message || "Failed to save treatment plan. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const prescriptionOptions = existingPrescriptions.map((p) => ({
    id: p.id,
    name:
      p.data?.prescriptionItems?.[0]?.medication ||
      p.data?.items?.[0]?.medication ||
      p.data?.items?.[0]?.name ||
      "Unnamed Prescription",
    dosage:
      p.data?.prescriptionItems?.[0]?.dosage || p.data?.items?.[0]?.dosage,
    quantity:
      p.data?.prescriptionItems?.[0]?.quantity || p.data?.items?.[0]?.quantity,
  }));

  // Summary helpers
  const getGoalsSummary = () => {
    const count = formData.goals?.length || 0;
    if (count === 0) return "No goals added";
    const achieved =
      formData.goals?.filter((g) => g.status === "achieved").length || 0;
    return `${count} goal${count !== 1 ? "s" : ""} (${achieved} achieved)`;
  };

  const getAppointmentsSummary = () => {
    const count = formData.followUpAppointments?.length || 0;
    if (count === 0) return "No appointments scheduled";
    return `${count} appointment${count !== 1 ? "s" : ""} scheduled`;
  };

  const getPrescriptionsSummary = () => {
    const count = formData.linkedPrescriptions?.length || 0;
    if (count === 0) return "No prescriptions linked";
    return `${count} prescription${count !== 1 ? "s" : ""} linked`;
  };

  const getSubscriptionSummary = () => {
    if (!formData.subscriptionPlan) return "No subscription plan";
    return `${
      formData.subscriptionPlan.prescriptionName
    } - £${formData.subscriptionPlan.pricePerSupply.toFixed(2)} ${
      formData.subscriptionPlan.billingFrequency
    }`;
  };

  const handleAIAutoCreate = async () => {
    if (!consultationNotes && !summaryText) {
      toast({
        title: "No Consultation Notes",
        description:
          "Please add consultation notes or summary before using AI auto-create",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingWithAI(true);

    try {
      // Simulate AI processing delay
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const combinedNotes = `${summaryText}\n\n${consultationNotes}`.trim();

      // AI Logic: Parse notes and extract key information
      const generatedPlan = generateTreatmentPlanFromNotes(
        combinedNotes,
        selectedPatient,
      );

      // Update form data with AI-generated plan
      updateFormData({
        name: generatedPlan.name,
        description: generatedPlan.description,
        startDate: generatedPlan.startDate,
        endDate: generatedPlan.endDate,
      });

      // Add generated goals
      generatedPlan.goals.forEach((goal) => addGoal(goal));

      // Expand all sections to show generated content
      setOpenAccordionItems(["goals", "appointments", "prescriptions"]);

      toast({
        title: "AI Plan Generated",
        description:
          "Treatment plan has been created from consultation notes. Please review and adjust as needed.",
      });
    } catch (error) {
      toast({
        title: "Generation Failed",
        description: "Failed to generate treatment plan. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingWithAI(false);
    }
  };

  const generateTreatmentPlanFromNotes = (notes: string, patient: any) => {
    const lowerNotes = notes.toLowerCase();

    // Extract plan name based on conditions mentioned
    let planName = "Comprehensive Care Plan";
    if (lowerNotes.includes("diabetes")) planName = "Diabetes Management Plan";
    else if (
      lowerNotes.includes("hypertension") ||
      lowerNotes.includes("blood pressure")
    )
      planName = "Hypertension Control Plan";
    else if (lowerNotes.includes("weight") || lowerNotes.includes("obesity"))
      planName = "Weight Management Plan";
    else if (lowerNotes.includes("asthma") || lowerNotes.includes("copd"))
      planName = "Respiratory Care Plan";
    else if (lowerNotes.includes("pain")) planName = "Pain Management Plan";

    // Extract description
    const descriptionMatch = notes.match(
      /(?:plan|summary|assessment)[:\s]+([^\.]+)/i,
    );
    const description = descriptionMatch
      ? descriptionMatch[1].trim()
      : "Comprehensive treatment plan based on consultation findings";

    // Generate start and end dates
    const startDate = new Date().toISOString().split("T")[0];
    const endDate = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0]; // 90 days from now

    // Generate goals based on mentioned vitals and targets
    const goals: any[] = [];

    // Weight goals
    if (
      lowerNotes.includes("weight") &&
      (lowerNotes.includes("reduce") ||
        lowerNotes.includes("lose") ||
        lowerNotes.includes("target"))
    ) {
      const weightMatch = notes.match(
        /(?:target|goal|reduce to|lose)\s+(\d+)\s*kg/i,
      );
      const targetWeight = weightMatch ? weightMatch[1] : "80";
      goals.push({
        id: uuidv4(),
        description: `Achieve target weight of ${targetWeight}kg`,
        linkedVitalType: "weight" as any,
        targetValue: targetWeight,
        targetDate: endDate,
        status: "pending" as any,
        notes: "Monitor weight weekly and adjust diet/exercise as needed",
        createdAt: new Date().toISOString(),
      });
    }

    // Blood pressure goals
    if (
      lowerNotes.includes("blood pressure") ||
      lowerNotes.includes("bp") ||
      lowerNotes.includes("hypertension")
    ) {
      const bpMatch = notes.match(/(?:target|goal|reduce to)\s+(\d+\/\d+)/i);
      const targetBP = bpMatch ? bpMatch[1] : "120/80";
      goals.push({
        id: uuidv4(),
        description: `Maintain blood pressure below ${targetBP} mmHg`,
        linkedVitalType: "blood-pressure" as any,
        targetValue: targetBP,
        targetDate: endDate,
        status: "pending" as any,
        notes:
          "Monitor blood pressure regularly and ensure medication adherence",
        createdAt: new Date().toISOString(),
      });
    }

    // Blood glucose goals
    if (
      lowerNotes.includes("glucose") ||
      lowerNotes.includes("diabetes") ||
      lowerNotes.includes("hba1c")
    ) {
      const glucoseMatch = notes.match(
        /(?:target|goal|reduce to)\s+(\d+\.?\d*)\s*(?:mmol|mg)/i,
      );
      const targetGlucose = glucoseMatch ? glucoseMatch[1] : "5.5";
      goals.push({
        id: uuidv4(),
        description: `Achieve fasting blood glucose below ${targetGlucose} mmol/L`,
        linkedVitalType: "blood-glucose" as any,
        targetValue: targetGlucose,
        targetDate: endDate,
        status: "pending" as any,
        notes:
          "Monitor blood glucose levels daily and maintain medication schedule",
        createdAt: new Date().toISOString(),
      });
    }

    // Generic medication adherence goal
    if (
      lowerNotes.includes("medication") ||
      lowerNotes.includes("prescription")
    ) {
      goals.push({
        id: uuidv4(),
        description: "Maintain 100% medication adherence",
        targetValue: "100%",
        targetDate: endDate,
        status: "pending" as any,
        notes: "Take all prescribed medications as directed",
        createdAt: new Date().toISOString(),
      });
    }

    // If no specific goals found, add a general health improvement goal
    if (goals.length === 0) {
      goals.push({
        id: uuidv4(),
        description: "Improve overall health and wellbeing",
        targetValue: "Achieved",
        targetDate: endDate,
        status: "pending" as any,
        notes:
          "Follow treatment recommendations and attend all scheduled appointments",
        createdAt: new Date().toISOString(),
      });
    }

    return {
      name: planName,
      description,
      startDate,
      endDate,
      goals,
    };
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          handleClose();
        } else {
          setOpen(true);
        }
      }}
    >
      {trigger && (
        <div onClick={() => setOpen(true)} className="cursor-pointer">
          {trigger}
        </div>
      )}
      <DialogContent
        className={cn(
          "max-w-4xl max-h-[90vh] flex flex-col",
          isDarkMode
            ? "bg-gray-900 border-gray-700 text-gray-200"
            : "bg-white border-gray-200",
        )}
      >
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <DialogTitle
                  className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                >
                  {editMode ? "Edit Treatment Plan" : "Create Treatment Plan"}
                </DialogTitle>
                {isViewingDraft && (
                  <Badge
                    className={cn(
                      "text-xs font-semibold",
                      isDarkMode
                        ? "bg-gray-600 hover:bg-gray-700 text-white"
                        : "bg-gray-600 hover:bg-gray-700 text-white",
                    )}
                  >
                    🔄 DRAFT MODE
                  </Badge>
                )}
              </div>
              <DialogDescription
                className={isDarkMode ? "text-gray-400" : "text-gray-600"}
              >
                {isViewingDraft
                  ? "Review and finalize the treatment plan details below. All sections are marked as draft until saved."
                  : editMode
                  ? "Update the treatment plan details below"
                  : "Create a comprehensive treatment plan for your patient"}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto pr-2 -mr-2">
          <div className="space-y-4">
            {/* Plan Overview - Always visible, not in accordion */}
            <div
              className={cn(
                "rounded-lg border p-4",
                isDarkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-gray-50 border-gray-200",
              )}
            >
              <div className="flex items-center gap-2 mb-4">
                <FileText
                  className={cn(
                    "w-5 h-5",
                    isDarkMode ? "text-gray-400" : "text-gray-500",
                  )}
                />
                <h3
                  className={cn(
                    "text-base font-semibold",
                    isDarkMode ? "text-white" : "text-gray-900",
                  )}
                >
                  Plan Overview
                </h3>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-xs",
                    isDarkMode
                      ? "bg-gray-700 text-gray-300 border-gray-600"
                      : "bg-gray-100 text-gray-700 border-gray-300",
                  )}
                >
                  Required
                </Badge>
                {isViewingDraft && (formData.name || formData.description) && (
                  <Badge
                    className={cn(
                      "text-xs",
                      isDarkMode
                        ? "bg-gray-600 hover:bg-gray-700 text-white"
                        : "bg-gray-600 hover:bg-gray-700 text-white",
                    )}
                  >
                    🔄 Draft - Review Required
                  </Badge>
                )}
              </div>
              <PlanOverviewSection
                formData={{
                  name: formData.name || "",
                  description: formData.description || "",
                  startDate: formData.startDate || "",
                  endDate: formData.endDate || "",
                  status: normalizePlanStatus(formData.status) as any,
                }}
                onUpdate={updateFormData}
                isDarkMode={isDarkMode}
              />
            </div>

            {/* Calculator Results Section - Show if results exist */}
            {(draftData?.calculatorResults || formData?.calculatorResults) &&
              (draftData?.calculatorResults?.length > 0 ||
                formData?.calculatorResults?.length > 0) && (
                <div
                  className={cn(
                    "rounded-lg border p-4",
                    isDarkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-gray-50 border-gray-200",
                  )}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles
                      className={cn(
                        "w-5 h-5",
                        isDarkMode ? "text-gray-400" : "text-gray-500",
                      )}
                    />
                    <h3
                      className={cn(
                        "text-base font-semibold",
                        isDarkMode ? "text-white" : "text-gray-900",
                      )}
                    >
                      Calculator Recommendations
                    </h3>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-xs",
                        isDarkMode
                          ? "bg-gray-700 text-gray-300 border-gray-600"
                          : "bg-gray-100 text-gray-700 border-gray-300",
                      )}
                    >
                      Dosage Calculators
                    </Badge>
                    {isViewingDraft &&
                      draftData?.calculatorResults &&
                      draftData.calculatorResults.length > 0 && (
                        <Badge
                          className={cn(
                            "text-xs",
                            isDarkMode
                              ? "bg-gray-600 hover:bg-gray-700 text-white"
                              : "bg-gray-600 hover:bg-gray-700 text-white",
                          )}
                        >
                          Draft
                        </Badge>
                      )}
                  </div>
                  <div className="space-y-3">
                    {(
                      draftData?.calculatorResults ||
                      formData?.calculatorResults
                    )?.map((result: any, idx: number) => (
                      <div
                        key={idx}
                        className={cn(
                          "rounded p-3",
                          isDarkMode ? "bg-gray-700/50" : "bg-gray-100",
                        )}
                      >
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p
                              className={cn(
                                "text-xs font-medium mb-1",
                                isDarkMode ? "text-gray-400" : "text-gray-600",
                              )}
                            >
                              Medication/Vaccine
                            </p>
                            <p
                              className={cn(
                                "font-semibold",
                                isDarkMode ? "text-gray-200" : "text-gray-900",
                              )}
                            >
                              {result.medication}
                            </p>
                          </div>
                          <div>
                            <p
                              className={cn(
                                "text-xs font-medium mb-1",
                                isDarkMode ? "text-gray-400" : "text-gray-600",
                              )}
                            >
                              Dosage
                            </p>
                            <p
                              className={cn(
                                "font-semibold",
                                isDarkMode ? "text-gray-200" : "text-gray-900",
                              )}
                            >
                              {result.dosage}
                            </p>
                          </div>
                          <div>
                            <p
                              className={cn(
                                "text-xs font-medium mb-1",
                                isDarkMode ? "text-gray-400" : "text-gray-600",
                              )}
                            >
                              Quantity
                            </p>
                            <p
                              className={cn(
                                "font-semibold",
                                isDarkMode ? "text-gray-200" : "text-gray-900",
                              )}
                            >
                              {result.quantity}
                            </p>
                          </div>
                          {result.duration && (
                            <div>
                              <p
                                className={cn(
                                  "text-xs font-medium mb-1",
                                  isDarkMode
                                    ? "text-gray-400"
                                    : "text-gray-600",
                                )}
                              >
                                Schedule/Duration
                              </p>
                              <p
                                className={cn(
                                  "font-semibold",
                                  isDarkMode
                                    ? "text-gray-200"
                                    : "text-gray-900",
                                )}
                              >
                                {result.duration}
                              </p>
                            </div>
                          )}
                        </div>
                        {result.notes && (
                          <div className="mt-2">
                            <p
                              className={cn(
                                "text-xs font-medium mb-1",
                                isDarkMode ? "text-gray-400" : "text-gray-600",
                              )}
                            >
                              Notes
                            </p>
                            <p
                              className={cn(
                                "text-sm",
                                isDarkMode ? "text-gray-300" : "text-gray-700",
                              )}
                            >
                              {result.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Accordion Sections */}
            <Accordion
              type="multiple"
              className="space-y-2"
              value={openAccordionItems}
              onValueChange={setOpenAccordionItems}
            >
              {/* Goals Section */}
              <AccordionItem
                value="goals"
                className={cn(
                  "rounded-lg border",
                  isDarkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-gray-50 border-gray-200",
                )}
              >
                <AccordionTrigger
                  className={cn(
                    "px-4 py-3 hover:no-underline",
                    isDarkMode ? "hover:bg-gray-750" : "hover:bg-gray-100",
                  )}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Target
                      className={cn(
                        "w-5 h-5",
                        isDarkMode ? "text-gray-400" : "text-gray-500",
                      )}
                    />
                    <div className="flex-1 text-left">
                      <h3
                        className={cn(
                          "text-sm font-semibold",
                          isDarkMode ? "text-white" : "text-gray-900",
                        )}
                      >
                        Treatment Goals
                      </h3>
                      <p
                        className={cn(
                          "text-xs",
                          isDarkMode ? "text-gray-400" : "text-gray-600",
                        )}
                      >
                        {getGoalsSummary()}
                      </p>
                    </div>
                    {isViewingDraft &&
                      formData.goals &&
                      formData.goals.length > 0 && (
                        <Badge
                          className={cn(
                            "text-xs",
                            isDarkMode
                              ? "bg-gray-600 hover:bg-gray-700 text-white"
                              : "bg-gray-600 hover:bg-gray-700 text-white",
                          )}
                        >
                          Draft
                        </Badge>
                      )}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsAddingGoal(true);
                        // Expand the accordion if not already open
                        if (!openAccordionItems.includes("goals")) {
                          setOpenAccordionItems([
                            ...openAccordionItems,
                            "goals",
                          ]);
                        }
                      }}
                      className={cn(
                        "h-7 px-2 text-xs opacity-60 hover:opacity-100 transition-opacity cursor-pointer rounded-md flex items-center gap-1",
                        isDarkMode
                          ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100",
                      )}
                    >
                      <Plus className="w-3 h-3" />
                      Add
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <GoalsSection
                    goals={formData.goals || []}
                    addGoal={addGoal}
                    updateGoal={updateGoal}
                    removeGoal={removeGoal}
                    isDarkMode={isDarkMode}
                    isAdding={isAddingGoal}
                    setIsAdding={setIsAddingGoal}
                    pendingGoal={draftData?.pendingGoals?.[0]}
                    consultationStatus={
                      editMode
                        ? consultationData?.consultationStatus
                        : undefined
                    }
                    editMode={editMode}
                    stateConsultationId={stateConsultationId}
                  />
                </AccordionContent>
              </AccordionItem>

              {/* Follow-up Appointments Section */}
              <AccordionItem
                value="appointments"
                className={cn(
                  "rounded-lg border",
                  isDarkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-gray-50 border-gray-200",
                )}
              >
                <AccordionTrigger
                  className={cn(
                    "px-4 py-3 hover:no-underline",
                    isDarkMode ? "hover:bg-gray-750" : "hover:bg-gray-100",
                  )}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Calendar
                      className={cn(
                        "w-5 h-5",
                        isDarkMode ? "text-gray-400" : "text-gray-500",
                      )}
                    />
                    <div className="flex-1 text-left">
                      <h3
                        className={cn(
                          "text-sm font-semibold",
                          isDarkMode ? "text-white" : "text-gray-900",
                        )}
                      >
                        Follow-up Appointments
                      </h3>
                      <p
                        className={cn(
                          "text-xs",
                          isDarkMode ? "text-gray-400" : "text-gray-600",
                        )}
                      >
                        {getAppointmentsSummary()}
                      </p>
                    </div>
                    {isViewingDraft &&
                      formData.followUpAppointments &&
                      formData.followUpAppointments.length > 0 && (
                        <Badge
                          className={cn(
                            "text-xs",
                            isDarkMode
                              ? "bg-gray-600 hover:bg-gray-700 text-white"
                              : "bg-gray-600 hover:bg-gray-700 text-white",
                          )}
                        >
                          Draft
                        </Badge>
                      )}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsAddingAppointment(true);
                        // Expand the accordion if not already open
                        if (!openAccordionItems.includes("appointments")) {
                          setOpenAccordionItems([
                            ...openAccordionItems,
                            "appointments",
                          ]);
                        }
                      }}
                      className={cn(
                        "h-7 px-2 text-xs opacity-60 hover:opacity-100 transition-opacity cursor-pointer rounded-md flex items-center gap-1",
                        isDarkMode
                          ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100",
                      )}
                    >
                      <Plus className="w-3 h-3" />
                      Add
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <FollowUpSection
                    followUpAppointments={formData.followUpAppointments || []}
                    addFollowUpAppointment={addFollowUpAppointment}
                    removeFollowUpAppointment={removeFollowUpAppointment}
                    updateFollowUpAppointment={updateFollowUpAppointment}
                    schedulePharmacists={schedulePharmacists}
                    scheduleRoomNames={scheduleRoomNames}
                    isDarkMode={isDarkMode}
                    isAdding={isAddingAppointment}
                    setIsAdding={setIsAddingAppointment}
                    editMode={editMode}
                    onDeleteInEditMode={handleDeleteFollowUpInEditMode}
                    existingAppointments={[
                      ...existingAppointments,
                      ...(draftData?._enrichedAppointments || []),
                    ]}
                    pendingAppointment={draftData?.pendingAppointments?.[0]}
                    services={services}
                    consultationStatus={
                      editMode
                        ? consultationData?.consultationStatus
                        : undefined
                    }
                    stateConsultationId={stateConsultationId}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        {/* Footer with Save Button */}
        <DialogFooter className="flex-shrink-0 mt-4">
          <Button
            variant="outline"
            onClick={handleClose}
            className={
              isDarkMode
                ? "border-gray-600 text-gray-300 hover:bg-gray-800"
                : ""
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              isSaving ||
              (editMode &&
                stateConsultationId &&
                consultationData?.consultationStatus === 1)
            }
            className="gap-2 bg-black hover:bg-gray-900 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                {editMode ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {editMode ? "Update Plan" : "Create Plan"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
